import { useCallback, useEffect, useRef, useState } from "react";
import { getAblyClient } from "../utils/ablyManager";

// Connection states that mean "no realtime link right now".
export const ABLY_OFFLINE_STATES = [
  "disconnected",
  "suspended",
  "failed",
  "closed",
];

/**
 * Tracks the realtime link used for video-timestamp sync (ablyManager client)
 * plus the browser's own network status.
 *
 * Returns:
 *   offline    - render flag
 *   offlineRef - same value, readable synchronously inside event handlers
 *   retrying   - a manual retry is in flight
 *   retry      - forces an immediate reconnect attempt
 */
const useAblyConnectionStatus = () => {
  const [offline, setOffline] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const offlineRef = useRef(false);
  const retryTimerRef = useRef(null);
  const evaluateRef = useRef(null);

  useEffect(() => {
    const client = getAblyClient();
    if (!client || !client.connection) return;

    const evaluate = () => {
      const next =
        !navigator.onLine ||
        ABLY_OFFLINE_STATES.includes(client.connection.state);
      if (next === offlineRef.current) return;
      offlineRef.current = next;
      setOffline(next);
      if (!next) setRetrying(false);
    };

    evaluateRef.current = evaluate;
    client.connection.on(evaluate);
    window.addEventListener("online", evaluate);
    window.addEventListener("offline", evaluate);
    evaluate();

    return () => {
      client.connection.off(evaluate);
      window.removeEventListener("online", evaluate);
      window.removeEventListener("offline", evaluate);
      evaluateRef.current = null;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  // Ably auto-retries on its own; this just forces an immediate attempt.
  const retry = useCallback(() => {
    setRetrying(true);
    try {
      const client = getAblyClient();
      if (
        client?.connection &&
        ABLY_OFFLINE_STATES.includes(client.connection.state)
      ) {
        client.connection.connect();
      }
    } catch (err) {
      console.error("Ably reconnect failed", err);
    }
    if (evaluateRef.current) evaluateRef.current();
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => setRetrying(false), 6000);
  }, []);

  return { offline, offlineRef, retrying, retry };
};

export default useAblyConnectionStatus;
